'use client';

/**
 * API namespace `admin.market.reviews` (sub-namespace extracted).
 * Verbatim extraction from frontend/src/lib/api-client.ts (originally within admin.market).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2 (relative to admin.market), T-EXPORT, T-IMPORTS.
 */

import { apiFetch, apiFetchFormData } from '../core/fetch-client';
import type {
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
} from '../core/types';
import type {
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  Paginated,
} from '../core/shared-types';

export const reviews = {
  /**
   * POST /api/admin/market/reviews/:reviewId/moderate — admin hides
   * or restores a review. `action='hide'` sets `moderated=true` +
   * `moderatedBy` + `moderatedAt` + `moderationReason` (Review schema
   * — SPEC-084 §3.3). `action='restore'` clears them. The review text
   * + rating are NOT mutated (append-only — only the moderation flags
   * are mutable via a dedicated `updateOne` bypass path in
   * `review.service.moderateReview`).
   *
   * Authz: `requireSuperadminOrAdmin`. NO 2FA gate (moderation is a
   * reputational signal, not a financial op — does NOT move credits).
   *
   * Spec ref: SPEC-084 Fase 6 §5.2 (POST /admin/market/reviews/:reviewId/
   *           moderate); §3.3 (Review.moderated* fields); §4.3
   *           (moderateReview flow).
   */
  async moderate(
    reviewId: string,
    body: AdminModerateReviewBody,
    signal?: AbortSignal,
  ): Promise<Review> {
    return apiFetch<Review>(
      `/admin/market/reviews/${encodeURIComponent(reviewId)}/moderate`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        signal,
      },
    );
  },
};
